import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { LockKeyhole } from "lucide-react";

import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

/**
 * Tela de login do painel. Fica fora do layout protegido, senão o próprio
 * guarda mandaria o usuário de volta para cá em laço.
 */
export const Route = createFileRoute("/admin_/login")({
  head: () => ({ meta: [{ name: "robots", content: "noindex, nofollow" }, { title: "Entrar no painel" }] }),
  component: Login,
});

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => {
      if (data.user) {
        void supabase.from("user_roles").select("role").eq("user_id", data.user.id)
          .in("role", ["admin", "editor"]).limit(1)
          .then(({ data: roles }) => { if (roles?.length) void navigate({ to: "/admin/" }); });
      }
    });
  }, [navigate]);

  async function entrar(evento: React.FormEvent) {
    evento.preventDefault();
    setErro(null);
    setAviso(null);
    setOcupado(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password: senha });
    setOcupado(false);
    if (error) {
      setErro("E-mail ou senha incorretos.");
      return;
    }
    const { data: roles, error: roleError } = await supabase.from("user_roles").select("role")
      .eq("user_id", data.user.id).in("role", ["admin", "editor"]).limit(1);
    if (roleError || !roles?.length) {
      await supabase.auth.signOut();
      setErro("Esta conta não tem acesso ao painel.");
      return;
    }
    void navigate({ to: "/admin/" });
  }

  async function criarAcesso() {
    setErro(null);
    setAviso(null);
    setOcupado(true);
    const { error } = await supabase.auth.signUp({
      email: email.trim(),
      password: senha,
      options: { emailRedirectTo: `${window.location.origin}/admin/login` },
    });
    setOcupado(false);
    if (error) {
      setErro(error.message);
      return;
    }
    setAviso("Acesso criado. Confirme pelo link enviado ao seu e-mail e depois entre.");
  }

  const campo =
    "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <main className="admin-login">
      <section className="admin-login-card">
      <div className="admin-login-mark"><LockKeyhole className="size-5" aria-hidden="true" /></div>
      <h1 className="mt-6 text-xl font-semibold text-foreground">Painel de administração</h1>
      <p className="mt-2 text-sm text-muted-foreground">Entre para gerenciar o conteúdo e as configurações do site.</p>
      <form onSubmit={entrar} className="mt-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground" htmlFor="email">E-mail</label>
          <input id="email" type="email" className={campo} value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground" htmlFor="senha">Senha</label>
          <input id="senha" type="password" className={campo} value={senha} onChange={(e) => setSenha(e.target.value)} />
        </div>
        {erro ? <p className="text-xs font-medium text-destructive">{erro}</p> : null}
        {aviso ? <p className="text-xs text-muted-foreground">{aviso}</p> : null}
        <Button
          type="submit"
          disabled={ocupado}
          className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Entrar
        </Button>
        <Button
          type="button"
          onClick={criarAcesso}
          disabled={ocupado}
          variant="outline"
          className="w-full"
        >
          Criar acesso
        </Button>
      </form>
      </section>
    </main>
  );
}
